const TRAILS_API_URL = 'https://trails-api.sequence.app/rpc/Trails'

function getApiKey(): string {
  const key = process.env.NEXT_PUBLIC_TRAILS_API_KEY
  if (!key) throw new Error('NEXT_PUBLIC_TRAILS_API_KEY is not set')
  return key
}

function headers(): HeadersInit {
  return {
    'Content-Type': 'application/json',
    'X-Access-Key': getApiKey(),
  }
}

export interface TrailsQuoteRequest {
  ownerAddress: string
  originChainId: number
  originTokenAddress: string
  originTokenAmount: string
  destinationChainId: number
  destinationTokenAddress: string
  destinationToAddress: string
  tradeType?: 'EXACT_INPUT' | 'EXACT_OUTPUT'
  options?: {
    slippageTolerance?: number
  }
}

export interface TrailsDepositTransaction {
  toAddress: string
  tokenAddress: string
  amount: string
}

export interface TrailsQuote {
  fromAmount: string
  toAmount: string
}

export interface TrailsFees {
  totalFeeUsd: string
}

export interface TrailsIntent {
  intentId: string
  quote: TrailsQuote
  fees: TrailsFees
  expiresAt: string
  depositTransaction: TrailsDepositTransaction
}

export interface TrailsQuoteResponse {
  intent: TrailsIntent
}

export interface TrailsIntentReceipt {
  status: 'SUCCEEDED' | 'FAILED' | 'PENDING'
  originTxHash?: string
  destinationTxHash?: string
}

// Deduplicate concurrent QuoteIntent calls with the same parameters
const inflightQuotes = new Map<string, Promise<TrailsQuoteResponse>>()

export function quoteIntent(
  params: TrailsQuoteRequest,
): Promise<TrailsQuoteResponse> {
  // Key on the fields that affect the quote (ignore ownerAddress/destinationToAddress
  // since they don't change the pricing)
  const key = `${params.originChainId}:${params.originTokenAddress}:${params.originTokenAmount}:${params.destinationTokenAddress}`

  const inflight = inflightQuotes.get(key)
  if (inflight) return inflight

  const promise = (async () => {
    const res = await fetch(`${TRAILS_API_URL}/QuoteIntent`, {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify(params),
    })

    if (!res.ok) {
      const text = await res.text().catch(() => '')
      throw new Error(`QuoteIntent failed (${res.status}): ${text}`)
    }

    return res.json() as Promise<TrailsQuoteResponse>
  })().finally(() => {
    inflightQuotes.delete(key)
  })

  inflightQuotes.set(key, promise)
  return promise
}

export async function commitIntent(intent: any): Promise<{ intentId: string }> {
  const res = await fetch(`${TRAILS_API_URL}/CommitIntent`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ intent }),
  })

  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`CommitIntent failed (${res.status}): ${text}`)
  }

  const data = await res.json()
  // CommitIntent may return the intent nested inside an `intent` field
  return {
    intentId: data?.intent?.intentId ?? data?.intentId ?? intent.intentId,
  }
}

export async function executeIntent(
  intentId: string,
  walletAddress: string,
): Promise<void> {
  const res = await fetch(`${TRAILS_API_URL}/ExecuteIntent`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ intentId, walletAddress }),
  })

  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`ExecuteIntent failed (${res.status}): ${text}`)
  }
}

export async function waitIntentReceipt(
  intentId: string,
  { maxAttempts = 60, intervalMs = 3000 } = {},
): Promise<TrailsIntentReceipt> {
  for (let i = 0; i < maxAttempts; i++) {
    const res = await fetch(`${TRAILS_API_URL}/WaitIntentReceipt`, {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({ intentId }),
    })

    // 400 with "no rows" means the receipt isn't ready yet — keep polling
    if (!res.ok) {
      if (res.status === 400) {
        await new Promise((resolve) => setTimeout(resolve, intervalMs))
        continue
      }
      const text = await res.text().catch(() => '')
      throw new Error(`GetIntentReceipt failed (${res.status}): ${text}`)
    }

    const data = await res.json()
    const receipt: TrailsIntentReceipt = data.intentReceipt ?? data

    if (receipt.status === 'SUCCEEDED' || receipt.status === 'FAILED') {
      return receipt
    }

    await new Promise((resolve) => setTimeout(resolve, intervalMs))
  }

  throw new Error('Intent receipt polling timed out')
}
